import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../../components/ui/sheet";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Badge } from "../../components/ui/badge";
import { useEffect, useMemo, useState } from "react";
import { RMList } from "../../utils/interfaces";
import { Requirement } from "../../types/deals";
import apiService from "../../services/apiService";

const schema = z.object({
  type: z.enum(["Leasing", "Hybrid", "Smart EPP"]), // ✅ now it will hold ID
  products: z.string().min(1, "Select a product"),
  quantum: z.coerce.number().positive("Must be > 0"),
  tenure: z.coerce.number().positive("Must be > 0"),
  assetCategory: z.string().min(1, "Select asset category"),
  applicationRM: z.string().min(1, "Select RM"),
  dealNote: z.string().optional(),
});

interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
}

interface AssetCategory {
  id: number;
  name: string;
}

export type RequirementFormValues = z.infer<typeof schema>;


interface RequirementFormProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (values: RequirementFormValues) => void;
  requirement?: Requirement | null;
  recentQuantum?: number; // to show suggestion
  uniqueRmNames?: RMList[];
  lender_user_product_types?: LenderTypes[]
  LenderStatus: any;
  isSubmitting?: boolean;
}

export function RequirementForm({ open, onOpenChange, onSubmit, requirement, recentQuantum, uniqueRmNames, lender_user_product_types, LenderStatus, isSubmitting = false }: RequirementFormProps) {
  const [assetCategories, setAssetCategories] = useState<AssetCategory[]>([]);

  const form = useForm<RequirementFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { type: "Leasing", products: "", quantum: 0, tenure: 12, assetCategory: "", applicationRM: "" },
  });

  useEffect(() => {
    if (!open) return;

    apiService
      .get<{ data: AssetCategory[] }>("/lender/get-asset-categories-list")
      .then((res) => {
        if (Array.isArray(res.data)) setAssetCategories(res.data);
      })
      .catch(() => setAssetCategories([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      if (requirement) {
        form.reset({
          type: requirement.type,
          products: requirement.product_type_id ? String(requirement.product_type_id) : "",
          quantum: requirement.quantum ?? 0,
          tenure: requirement.tenure ?? 12,
          assetCategory: requirement.asset_category_id ? String(requirement.asset_category_id) : "",
          applicationRM: requirement.applicationRMId ? String(requirement.applicationRMId) : "",
          dealNote: requirement.dealNote ?? "",
        });
      } else {
        form.reset({
          type: "Leasing",
          products: "",
          quantum: 0,
          tenure: 12,
          assetCategory: "",
          applicationRM: "",
          dealNote: ""
        });
      }
    }
  }, [open, requirement, form]);


  const selectedType = form.watch("type"); // watch type
  const requirementTypes = useMemo(() => {
    const typeMap = new Map<string, number>();
    lender_user_product_types?.forEach((item) => {
      if (!typeMap.has(item.parent_type)) {
        typeMap.set(item.parent_type, item.id); // ✅ assign first product's id as type id
      }
    });
    return Array.from(typeMap.entries()); // [ ["Leasing", 1], ["Hybrid", 5] ]
  }, [lender_user_product_types]);

  // ✅ Filter products for selected type
  const productsForType = useMemo(() => {
    return lender_user_product_types?.filter((item) => item.parent_type === selectedType) || [];
  }, [selectedType, lender_user_product_types]);

  // ✅ Reset products when type changes (only in create mode; edit mode keeps product_type_id selected)
  useEffect(() => {
    if (requirement) return;
    form.setValue("products", "");
  }, [selectedType, form, requirement]);



  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{requirement ? "Edit Requirement" : "Add Requirement"}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full max-h-[calc(100vh-120px)] overflow-auto mt-4">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((vals) => onSubmit(vals))}
              className="space-y-6 flex-1"
            >
              {/* ✅ Requirement Type */}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Requirement Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={!!requirement}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {requirementTypes.map(([name, id]) => (
                          <SelectItem key={id} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* ✅ Products based on type (single selection) */}
              <FormField
                control={form.control}
                name="products"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!!requirement}
                        className="grid grid-cols-2 gap-2"
                      >
                        {productsForType.map((p) => (
                          <label
                            key={p.id}
                            className={`flex items-center gap-2 rounded-md border p-2 ${
                              requirement ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted/50"
                            }`}
                          >
                            <RadioGroupItem value={String(p.id)} id={`product-${p.id}`} />
                            <span className="text-sm">{p.name}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <div className="text-xs text-muted-foreground mt-2">
                      Selected: {field.value ? (
                        (() => {
                          const prod = productsForType.find((p) => String(p.id) === field.value);
                          return (
                            <Badge variant="secondary" className="mt-1">
                              {prod ? prod.name : field.value}
                            </Badge>
                          );
                        })()
                      ) : (
                        "None"
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="quantum"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Proposed Quantum</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Amount" {...field} />
                      </FormControl>
                      {recentQuantum ? (
                        <div className="text-xs text-muted-foreground mt-1">
                          Suggestion based on recent deals: <button type="button" className="underline" onClick={() => form.setValue("quantum", recentQuantum)}>{recentQuantum}</button>
                        </div>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tenure"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tenure (months)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="e.g. 24" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                  control={form.control}
                  name="assetCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asset Category</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Asset Category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {assetCategories.map((category) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              <FormField
                control={form.control}
                name="applicationRM"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application RM</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select RM" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {uniqueRmNames?.map((rm) => (
                          <SelectItem key={rm.id} value={String(rm.id)}>{rm.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />



              <FormField
                control={form.control}
                name="dealNote"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal Note</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder="Optional context" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="mt-auto pt-4">
                <SheetFooter>
                  <Button type="submit" variant="hero" disabled={isSubmitting}>
                    {isSubmitting
                      ? requirement
                        ? "Updating..."
                        : "Saving..."
                      : requirement
                        ? "Update Requirement"
                        : "Save Requirement"}
                  </Button>
                </SheetFooter>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
